import React from "react";
import {
    Box,
    Button,
    Flex,
    Icon,
    Text,
    useColorModeValue,
} from "@chakra-ui/react";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { MdBarChart, MdOutlineCalendarToday } from "react-icons/md";
import { RiArrowDownSFill, RiArrowUpSFill } from "react-icons/ri";
import dayjs from "dayjs";

import Card from "components/card/Card.js";
import LineChart from "components/charts/LineChart";
import { formatNumber } from "../../../Utils/Utils";

const normalizeRoasPoint = (point) => {
    const roas = Number.parseFloat(point?.roas ?? 0);

    return {
        day: point?.day,
        roas: Number.isFinite(roas) ? roas : 0,
    };
};

const formatRoas = (value) => formatNumber(Number(value || 0).toFixed(2));

export default function RoasPerDay({
    data = [],
    growthPercentage = 0,
    startDate,
    endDate,
    ...rest
}) {
    const textColor = useColorModeValue("secondaryGray.900", "white");
    const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const iconColor = useColorModeValue("brand.500", "white");

    const chartPoints = [...data]
        .map(normalizeRoasPoint)
        .filter((point) => point.day)
        .sort((left, right) => dayjs(left.day).valueOf() - dayjs(right.day).valueOf());

    const averageRoas =
        chartPoints.length > 0
            ? chartPoints.reduce((total, point) => total + point.roas, 0) / chartPoints.length
            : 0;

    const formattedRange =
        startDate && endDate
            ? `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")}`
            : "Maximum";

    const isPositiveGrowth = growthPercentage >= 0;
    const growthLabel =
        growthPercentage > 0 ? "On track" : growthPercentage < 0 ? "Not so good.." : "Stable";

    const lineChartDataRoas = [
        {
            name: "ROAS",
            data: chartPoints.map((point) => point.roas),
        },
    ];

    const lineChartOptionsRoas = {
        chart: {
            toolbar: {
                show: false,
            },
            dropShadow: {
                enabled: true,
                top: 13,
                left: 0,
                blur: 10,
                opacity: 0.1,
                color: "#4318FF",
            },
        },
        colors: ["#4318FF"],
        markers: {
            size: 0,
            colors: "white",
            strokeColors: "#7551FF",
            strokeWidth: 3,
            strokeOpacity: 0.9,
            strokeDashArray: 0,
            fillOpacity: 1,
            discrete: [],
            shape: "circle",
            radius: 2,
            offsetX: 0,
            offsetY: 0,
            showNullDataPoints: true,
        },
        tooltip: {
            theme: "dark",
            y: {
                formatter: (value) => formatRoas(value),
            },
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            curve: "smooth",
            type: "line",
        },
        xaxis: {
            type: "category",
            categories: chartPoints.map((point) => dayjs(point.day).format("MMM DD")),
            title: {
                text: "Date",
            },
            labels: {
                style: {
                    colors: "#A3AED0",
                    fontSize: "12px",
                    fontWeight: "500",
                },
            },
            axisBorder: {
                show: false,
            },
            axisTicks: {
                show: false,
            },
        },
        yaxis: {
            title: {
                text: "ROAS",
            },
            labels: {
                style: {
                    colors: "#CBD5E0",
                    fontSize: "14px",
                },
                formatter: (value) => Number(value).toFixed(2),
            },
        },
        legend: {
            show: false,
        },
        grid: {
            show: false,
            column: {
                color: ["#7551FF"],
                opacity: 0.5,
            },
        },
    };

    return (
        <Card
            justifyContent="center"
            align="center"
            direction="column"
            w="100%"
            mb="0px"
            {...rest}
        >
            <Flex justify="space-between" ps="0px" pe="20px" pt="5px" w="100%">
                <Flex align="center" w="100%">
                    <Button
                        bg={boxBg}
                        fontSize="sm"
                        fontWeight="500"
                        color={textColorSecondary}
                        borderRadius="7px"
                        leftIcon={<MdOutlineCalendarToday color={textColorSecondary} />}
                        isDisabled
                        _disabled={{ opacity: 1, cursor: "default" }}
                    >
                        {formattedRange}
                    </Button>
                    <Button
                        ms="auto"
                        align="center"
                        justifyContent="center"
                        bg={boxBg}
                        w="37px"
                        h="37px"
                        lineHeight="100%"
                        borderRadius="10px"
                        isDisabled
                        _disabled={{ opacity: 1, cursor: "default" }}
                    >
                        <Icon as={MdBarChart} color={iconColor} w="24px" h="24px" />
                    </Button>
                </Flex>
            </Flex>
            <Flex w="100%" flexDirection={{ base: "column", lg: "row" }}>
                <Flex flexDirection="column" me="20px" mt="28px">
                    <Text
                        color={textColor}
                        fontSize="34px"
                        textAlign="start"
                        fontWeight="700"
                        lineHeight="100%"
                    >
                        {formatRoas(averageRoas)}
                    </Text>
                    <Flex align="center" mb="20px">
                        <Text
                            color="secondaryGray.600"
                            fontSize="sm"
                            fontWeight="500"
                            mt="4px"
                            me="12px"
                        >
                            Average
                        </Text>
                        <Flex align="center">
                            <Icon
                                as={isPositiveGrowth ? RiArrowUpSFill : RiArrowDownSFill}
                                color={isPositiveGrowth ? "green.500" : "red.500"}
                                me="2px"
                                mt="2px"
                            />
                            <Text
                                color={isPositiveGrowth ? "green.500" : "red.500"}
                                fontSize="sm"
                                fontWeight="700"
                            >
                                {Number(growthPercentage || 0).toFixed(2)}%
                            </Text>
                        </Flex>
                    </Flex>
                    <Flex align="center">
                        <Icon
                            as={isPositiveGrowth ? IoCheckmarkCircle : IoCloseCircle}
                            color={isPositiveGrowth ? "green.500" : "red.500"}
                            me="4px"
                        />
                        <Text
                            color={isPositiveGrowth ? "green.500" : "red.500"}
                            fontSize="md"
                            fontWeight="700"
                        >
                            {growthLabel}
                        </Text>
                    </Flex>
                </Flex>
                <Box minH="260px" minW="75%" mt="auto">
                    {chartPoints.length > 0 ? (
                        <LineChart
                            chartData={lineChartDataRoas}
                            chartOptions={lineChartOptionsRoas}
                        />
                    ) : (
                        <Flex justify="center" align="center" h="100%">
                            <Text color="secondaryGray.600">
                                No ROAS data available for the selected interval.
                            </Text>
                        </Flex>
                    )}
                </Box>
            </Flex>
        </Card>
    );
}
